// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { AttributeValueModel } from "@insite/client-framework/Types/ApiModels";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { mergeWithCss } from "@vanmeter/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    maximumNumberAttributeTypes: number;
    extendedStyles?: ProductAttributesStyles;
}

type Props = OwnProps & HasProduct;

export interface ProductAttributesStyles {
    container?: GridContainerProps;
    attributeTypeGridItem?: GridItemProps;
    attributeValueGridItem?: GridItemProps;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueText?: TypographyPresentationProps;
    // XNG-Changes
    attributeValue?: TypographyPresentationProps;
}

export const productAttributesStyles: ProductAttributesStyles = {
    container: {
        gap: 0,
    },
    attributeTypeGridItem: {
        width: [6, 6, 6, 4, 4],
        css: css`
            overflow: hidden;
        `,
    },
    attributeValueGridItem: {
        width: [6, 6, 6, 8, 8],
        css: css`
            overflow: hidden;
        `,
    },
    attributeTypeText: {
        weight: "bold",
    },
};

const ProductAttributes: FC<Props> = ({ product: { attributeTypes }, maximumNumberAttributeTypes, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(productAttributesStyles, extendedStyles));

    if (!attributeTypes || attributeTypes.length === 0) {
        return null;
    }

    const maximum = Math.min(maximumNumberAttributeTypes, 100);
    const limitedAttributeTypes = attributeTypes.slice(0, maximum);

    const displayAttributeValues = (attributeValues: AttributeValueModel[] | null) => {
        return (
            <Typography {...styles.attributeValueText}>
                {(attributeValues ?? []).map(a => a.valueDisplay).join(", ")}
            </Typography>
        );
    };

    // XNG-Changes: Structure
    return (
        <StyledWrapper {...styles.container}>
            {limitedAttributeTypes.map(attributeType => (
                <StyledWrapper key={attributeType.id.toString()}>
                    <Typography {...styles.attributeTypeText} data-test-selector={`attributeType${attributeType.id}`}>
                        {attributeType.label || attributeType.name}
                    </Typography>
                    <Typography {...styles.attributeValue} data-test-selector={`attributeValuesFor${attributeType.id}`}>
                        {displayAttributeValues(attributeType.attributeValues)}
                    </Typography>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

export default withProduct(ProductAttributes);
